import React, { useState } from "react";
import { Printer, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FORMATS = [
  { id: "a4", label: "A4", hint: "210 × 297 mm" },
  { id: "letter", label: "US Letter", hint: "8.5 × 11 in" },
];

export default function ExportPdfDialog({ open, onOpenChange, onExport }) {
  const [format, setFormat] = useState("a4");
  const [planName, setPlanName] = useState("");
  const [busy, setBusy] = useState(false);

  const handleExport = async () => {
    setBusy(true);
    try {
      await onExport({ pageFormat: format, planName: planName.trim() });
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md" data-testid="export-pdf-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Printer className="h-5 w-5 text-amber-500" />
            Print-ready PDF
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Exports one PDF: cover, the 8, 16 and 32-division maps, the Vastu Purusha overlaid on your plan with the 16-direction dial, the plain floor plan, and a room placement analysis of the markers you added.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          <div className="space-y-1.5">
            <Label htmlFor="pdf-plan-name" className="text-xs text-slate-400">Plan title (optional)</Label>
            <Input
              id="pdf-plan-name"
              data-testid="pdf-plan-name-input"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="e.g. 3BHK Main House"
              className="bg-slate-950 border-slate-800 text-slate-100 h-10"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Page size</Label>
            <div className="grid grid-cols-2 gap-2">
              {FORMATS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`pdf-format-${f.id}`}
                  onClick={() => setFormat(f.id)}
                  className={`text-left p-3 rounded-lg border transition-colors ${
                    format === f.id
                      ? "border-amber-500 bg-amber-500/10"
                      : "border-slate-800 bg-slate-950 hover:border-slate-700"
                  }`}
                >
                  <div className="font-display text-sm font-semibold text-slate-100">{f.label}</div>
                  <div className="font-mono text-[10px] text-slate-500 mt-0.5">{f.hint}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-slate-900 border-slate-800 hover:bg-slate-800 text-slate-100">
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={busy}
            data-testid="pdf-export-confirm-button"
            className="bg-amber-500 hover:bg-amber-400 text-slate-950"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Printer className="h-4 w-4 mr-1.5" />Download PDF</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
